/**
 * The composer card — /create's focal point.
 *
 * Replaces the prototype's stack of a label, a 3-row textarea, and multiple rows of
 * chips that were always on screen. The shape now is:
 *
 *   prompt (large, quiet, the only thing you must fill in)
 *   ─────────────────────────────────────────────────────
 *   Select genre, voice and language                         [model selector]
 *   ─────────────────────────────────────────────────────
 *   Generate Music
 *
 * Everything it renders was already on the screen; nothing was added and nothing
 * removed. What changed is that the prompt stays the subject until the user asks
 * for the supporting controls, and the three related choices stay grouped in one
 * disclosure instead of splitting into a primary band and a secondary heading.
 *
 * Purely presentational, per the components/ rules: prompt/genre/vocal live in
 * createStore and arrive as props, because Music DNA and the Vault push values in
 * from other routes. Generation itself stays in useSongs().createSong.
 */
import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  Languages,
  Mic2,
  Music4,
  Music2,
  ShieldCheck,
} from 'lucide-react';
import { Btn, Chip } from '../ui';
import { PREMIUM_MUSIC_MODEL, type MusicGenerationModel } from '../../lib/musicGenerationModels';
import { c } from '../../styles/theme';
import SelectedBonusPacks from '../bonus/SelectedBonusPacks';

/** How a value is offered: a row of chips with a small label above it. */
export interface ComposerChoice {
  label: string;
  options: readonly string[];
  value: string;
  onChange: (value: string) => void;
  /** Whether pressing the active chip again clears the choice. */
  clearable?: boolean;
  /** Shown to the left of the label. */
  icon?: ReactNode;
  /**
   * Replaces the chips with a line of explanation, for a choice that does not apply.
   *
   * The Language row uses it: an instrumental track has no language, and the request
   * records "Instrumental" whatever is selected. Showing the row with the reason keeps
   * the setting discoverable and cannot be pressed to no effect — a live-looking
   * control that the request overrules is precisely the thing not to build.
   */
  note?: string;
  /** Shown beneath the chips when the current selection needs extra guidance. */
  caution?: string;
}

export interface ComposerProps {
  prompt: string;
  onPromptChange: (prompt: string) => void;
  /** Accessible name for the prompt textarea. Defaults to Create's wording. */
  promptLabel?: string;
  /** Visible placeholder inside the prompt textarea. Defaults to Create's wording. */
  promptPlaceholder?: string;
  /** Whether the prompt box itself can be collapsed. */
  promptCollapsible?: boolean;
  /** Visible label for the prompt disclosure. */
  promptToggleLabel?: string;
  /** Controlled open state for a collapsible prompt. */
  promptOpen?: boolean;
  /** Called when a collapsible prompt opens or closes. */
  onPromptOpenChange?: (open: boolean) => void;
  model?: {
    label: string;
    options: ReadonlyArray<{ value: MusicGenerationModel; label: string; locked?: boolean }>;
    value: MusicGenerationModel;
    onChange: (value: MusicGenerationModel) => void;
  };
  /** The first choices shown when the genre/voice/language section is open. */
  primary: ComposerChoice[];
  /** Additional choices shown beneath the primary rows in that same section. */
  advanced: ComposerChoice[];
  onGenerate: () => void | Promise<void>;
  /** True while THIS composer instance owns a generation in flight — disables and relabels it. */
  generating: boolean;
  /**
   * Disables the button WITHOUT claiming a generation is running.
   *
   * Separate from `generating` because that prop does two things — it disables the
   * button and relabels it "Generating…" — and other surfaces sometimes need the first
   * without the second: Image Music is off until an image is chosen, and any non-owning
   * prompt instance stays disabled while another one generates. A button that says
   * "Generating…" when nothing is happening here is a false status. `reason` supplies the
   * accessible explanation.
   */
  disabled?: boolean;
  /**
   * Why the button is disabled. Shown beside it, and linked with aria-describedby.
   *
   * Visible rather than a tooltip: a disabled button fires no pointer events, so a
   * Tooltip wrapped around one never opens, and a `title` on a disabled control is
   * unreliable across browsers. The sentence is short and it is the one thing the user
   * needs, so it is simply on the screen.
   */
  disabledReason?: string;
}

function ModelSelector({ model }: Pick<ComposerProps, 'model'>) {
  const [open, setOpen] = useState(false);
  const listboxId = useId();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const selectedOptionRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [open]);

  useEffect(() => {
    if (open) selectedOptionRef.current?.focus();
  }, [open]);

  if (!model) return null;
  const selectedLabel =
    model.options.find((option) => option.value === model.value)?.label ?? model.options[0]?.label;

  function moveOption(current: HTMLButtonElement, direction: 1 | -1) {
    const options = Array.from(
      wrapRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]:not(:disabled)') ?? []
    );
    const currentIndex = options.indexOf(current);
    options[(currentIndex + direction + options.length) % options.length]?.focus();
  }

  return (
    <div
      ref={wrapRef}
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || !open) return;
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }}
      style={{
        position: 'relative',
        marginLeft: 'auto',
        alignSelf: 'center',
        display: 'block',
        flexShrink: 0,
        width: '10ch',
        height: 20,
        minWidth: 0,
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-label={model.label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
          event.preventDefault();
          setOpen(true);
        }}
        style={{
          display: 'block',
          width: '100%',
          height: 20,
          color: c.text2,
          fontFamily: 'Inter',
          fontSize: 12,
          fontWeight: 500,
          lineHeight: '20px',
          textAlign: 'right',
          whiteSpace: 'nowrap',
          padding: 0,
          border: 0,
          background: 'transparent',
          cursor: 'pointer',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 4,
          }}
        >
          {model.value === PREMIUM_MUSIC_MODEL && (
            <ShieldCheck size={12} color={c.accent} aria-hidden="true" />
          )}
          <span className="lyx-composer-model-value">{selectedLabel}</span>
        </span>
      </button>

      {open && (
        <div
          id={listboxId}
          role="listbox"
          aria-label={model.label}
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            zIndex: 20,
            width: 112,
            maxWidth: 'calc(100vw - 44px)',
            padding: 4,
            border: 0,
            borderRadius: 6,
            background: c.surface1,
            boxShadow: '0 8px 20px rgba(0,0,0,0.32)',
          }}
        >
          <p
            aria-hidden="true"
            style={{
              margin: '3px 8px 6px',
              color: c.text3,
              fontFamily: 'Inter',
              fontSize: 10,
              lineHeight: 1.2,
              textAlign: 'left',
            }}
          >
            Select model
          </p>
          {model.options.map((option) => {
            const selected = option.value === model.value;
            return (
              <button
                key={option.value}
                ref={selected ? selectedOptionRef : undefined}
                type="button"
                role="option"
                aria-selected={selected}
                disabled={option.locked}
                title={option.locked ? 'Available with Lyrixsa AI Professional (OTO 1)' : undefined}
                onClick={() => {
                  model.onChange(option.value);
                  setOpen(false);
                  triggerRef.current?.focus();
                }}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                    event.preventDefault();
                    moveOption(event.currentTarget, event.key === 'ArrowDown' ? 1 : -1);
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 5,
                  width: '100%',
                  padding: '6px 8px',
                  border: 0,
                  borderRadius: 4,
                  background: 'transparent',
                  color: selected ? c.accent : c.text2,
                  fontFamily: 'Inter',
                  fontSize: 12,
                  lineHeight: 1.2,
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                  cursor: option.locked ? 'not-allowed' : 'pointer',
                  opacity: option.locked ? 0.55 : 1,
                }}
              >
                {option.value === PREMIUM_MUSIC_MODEL && (
                  <ShieldCheck size={12} color={c.accent} aria-hidden="true" />
                )}
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Label + control row. One shape for both bands, so the two read as siblings. */
function ChoiceRow({
  label,
  options,
  value,
  onChange,
  clearable,
  icon,
  note,
  caution,
}: ComposerChoice) {
  return (
    <div>
      <p
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          fontSize: 11,
          color: c.text2,
          margin: '0 0 7px',
        }}
      >
        {icon}
        {label}
      </p>
      {note ? (
        // text2. The earlier note here reasoned from a 3:1 threshold, but 3:1 is the
        // NON-TEXT minimum — a 12px line needs 4.5:1, which text3 does not reach on
        // any surface. It stays quiet through its size rather than its colour.
        <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>{note}</p>
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {options.map((option) => (
              <Chip
                key={option}
                label={option}
                active={value === option}
                onClick={() => onChange(clearable && value === option ? '' : option)}
              />
            ))}
          </div>
          {caution && (
            <p
              role="status"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: c.accent,
                margin: '8px 0 0',
                lineHeight: 1.45,
              }}
            >
              <AlertTriangle size={13} color={c.accent} aria-hidden="true" />
              {caution}
            </p>
          )}
        </>
      )}
    </div>
  );
}

export default function Composer({
  prompt,
  onPromptChange,
  promptLabel = 'Describe the song you want to create',
  promptPlaceholder = 'Describe the song you want to create',
  promptCollapsible = false,
  promptToggleLabel = 'Description',
  promptOpen,
  onPromptOpenChange,
  model,
  primary,
  advanced,
  onGenerate,
  generating,
  disabled = false,
  disabledReason,
}: ComposerProps) {
  const promptId = useId();
  const promptPanelId = useId();
  const controlsId = useId();
  const reasonId = useId();
  const [controlsOpen, setControlsOpen] = useState(false);
  const [localPromptOpen, setLocalPromptOpen] = useState(false);

  const promptSectionOpen = promptCollapsible ? (promptOpen ?? localPromptOpen) : true;
  const collapsedControlsCaution = controlsOpen
    ? undefined
    : [...primary, ...advanced].find((choice) => choice.caution)?.caution;

  function setPromptSectionOpen(open: boolean) {
    if (!promptCollapsible) return;
    if (onPromptOpenChange) {
      onPromptOpenChange(open);
      return;
    }
    setLocalPromptOpen(open);
  }

  function handlePromptKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.nativeEvent.isComposing) return;
    if (event.key !== 'Enter') return;
    if (event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) return;
    if (generating || disabled) return;
    event.preventDefault();
    void onGenerate();
  }

  return (
    <div
      style={{
        background: c.surface1,
        border: `1px solid ${c.border}`,
        borderRadius: 14,
        // Deliberately generous. This is the one card on the screen that should
        // feel like a place to work rather than a form to fill in.
        padding: '20px 22px 18px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
      }}
    >
      {/* ---- the prompt ---- */}
      {/* The label is available to screen readers but not drawn: the placeholder
          already says what the field is for, and a visible "Describe your song"
          above a field whose placeholder repeats it is noise. */}
      <SelectedBonusPacks />
      {promptCollapsible ? (
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={() => setPromptSectionOpen(!promptSectionOpen)}
              aria-expanded={promptSectionOpen}
              aria-controls={promptPanelId}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: 0,
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                color: c.text1,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontFamily: 'Inter',
                  color: c.text2,
                  fontWeight: 500,
                }}
              >
                {promptToggleLabel}
              </span>
              <ChevronDown
                size={13}
                color={c.text2}
                aria-hidden="true"
                style={{
                  transform: promptSectionOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'transform 140ms ease',
                  flexShrink: 0,
                }}
              />
            </button>
          </div>

          {promptSectionOpen && (
            <div id={promptPanelId} style={{ paddingTop: 14 }}>
              <label htmlFor={promptId} className="lyx-visually-hidden">
                {promptLabel}
              </label>
              <textarea
                id={promptId}
                rows={3}
                value={prompt}
                onChange={(e) => onPromptChange(e.target.value)}
                onKeyDown={handlePromptKeyDown}
                placeholder={promptPlaceholder}
                className="lyx-composer-prompt"
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: 'none',
                  resize: 'none',
                  fontSize: 16,
                  lineHeight: 1.55,
                  padding: 0,
                  color: c.text1,
                }}
              />
            </div>
          )}
        </div>
      ) : (
        <>
          <label htmlFor={promptId} className="lyx-visually-hidden">
            {promptLabel}
          </label>
          <textarea
            id={promptId}
            rows={3}
            value={prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            onKeyDown={handlePromptKeyDown}
            placeholder={promptPlaceholder}
            className="lyx-composer-prompt"
            style={{
              width: '100%',
              background: 'transparent',
              border: 'none',
              resize: 'none',
              // Larger than the surrounding UI on purpose — the prompt is the subject
              // of this screen, so it reads as content rather than as an input.
              fontSize: 16,
              lineHeight: 1.55,
              padding: 0,
              color: c.text1,
            }}
          />
        </>
      )}

      {/* ---- controls ---- */}
      {(primary.length > 0 || advanced.length > 0 || model) && (
        <div
          style={{
            borderTop: `1px solid ${c.border}`,
            marginTop: 16,
            paddingTop: 16,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
              width: '100%',
              minHeight: 20,
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            <button
              type="button"
              onClick={() => setControlsOpen((open) => !open)}
              aria-expanded={controlsOpen}
              aria-controls={controlsId}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: 0,
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontFamily: 'Inter',
                  color: c.text2,
                  fontWeight: 500,
                }}
              >
                Select genre, voice and language
              </span>
              <ChevronDown
                size={13}
                color={c.text2}
                aria-hidden="true"
                style={{
                  transform: controlsOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'transform 140ms ease',
                  flexShrink: 0,
                }}
              />
            </button>
            {collapsedControlsCaution && (
              <span
                role="status"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  color: c.accent,
                  fontSize: 11.5,
                  lineHeight: 1.35,
                }}
              >
                <AlertTriangle size={13} color={c.accent} aria-hidden="true" />
                {collapsedControlsCaution}
              </span>
            )}
            <ModelSelector model={model} />
          </div>

          {controlsOpen && (
            <div
              id={controlsId}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
                paddingTop: 14,
              }}
            >
              {primary.map((choice) => (
                <ChoiceRow key={choice.label} {...choice} />
              ))}
              {advanced.map((choice) => (
                <ChoiceRow key={choice.label} {...choice} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ---- generate ----
          The flex layout of this row lives in global.css rather than here: it has to
          change direction below 560px, and an inline `alignItems` would outrank the
          media query trying to restack it. */}
      <div
        className="lyx-composer-actions"
        style={{
          borderTop: `1px solid ${c.border}`,
          marginTop: 16,
          paddingTop: 16,
        }}
      >
        {/* `generating` relabels; `disabled` only disables. Both switch the button
            off, and the label still says what the button does when it is merely
            unavailable — see the props. */}
        <Btn
          onClick={onGenerate}
          disabled={generating || disabled}
          aria-describedby={!generating && disabled && disabledReason ? reasonId : undefined}
          style={{ padding: '10px 20px' }}
        >
          <Music2 size={14} />
          {generating ? 'Generating music…' : 'Generate Music'}
        </Btn>
        {!generating && disabled && disabledReason ? (
          <span id={reasonId} style={{ marginTop: -8, fontSize: 11.5, color: c.text2, lineHeight: 1.5 }}>
            {disabledReason}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** Icons for the standard composer rows, so call sites stay declarative. */
export const COMPOSER_ICONS = {
  genre: <Music4 size={12} color={c.text2} />,
  vocal: <Mic2 size={12} color={c.text2} />,
  language: <Languages size={12} color={c.text2} />,
} as const;
